import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { EnvironmentAnalytics, Project } from "../types";
// Typed errors so HTTP status/code/details survive past the API boundary.
import { ApiError, NetworkError } from "./errors";
import { createLogger } from "./logger";

const log = createLogger("api");

// Additional API-specific types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  defaultEnvironment?: string;
}

export interface CreateEnvironmentRequest {
  projectId: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  variables?: Record<string, string>;
  customDomains?: string[];
  defaultCpuLimit?: string;
  defaultMemoryLimit?: string;
  defaultReplicas?: number;
  autoDeploy?: boolean;
  healthCheckPath?: string;
  healthCheckPort?: number;
  k8sNamespace?: string;
  k8sClusterId?: string;
}

export interface UpdateEnvironmentRequest {
  name?: string;
  description?: string;
  isDefault?: boolean;
  variables?: Record<string, string>;
  customDomains?: string[];
  defaultCpuLimit?: string;
  defaultMemoryLimit?: string;
  defaultReplicas?: number;
  autoDeploy?: boolean;
  healthCheckPath?: string;
  healthCheckPort?: number;
  k8sNamespace?: string;
  k8sClusterId?: string;
}

export interface Environment {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  variables?: Record<string, string>;
  customDomains?: string[];
  defaultCpuLimit?: string;
  defaultMemoryLimit?: string;
  defaultReplicas?: number;
  autoDeploy?: boolean;
  healthCheckPath?: string;
  healthCheckPort?: number;
  k8sNamespace?: string;
  k8sClusterId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnvironmentsResponse {
  environments: Environment[];
}

export interface UpdateProjectRequest {
  name: string;
  description?: string;
}

export interface ProjectsResponse {
  projects: Project[];
}

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8003/api";

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        // Try both token keys for compatibility
        const token =
          localStorage.getItem("avahana-token") ||
          localStorage.getItem("token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear both possible token keys
          localStorage.removeItem("token");
          localStorage.removeItem("avahana-token");
          localStorage.removeItem("user");
          // Redirect to login or trigger auth context update
          window.location.href = "/login";
        }

        // 403 on a project-scoped route means the user's role was changed or they
        // were removed while their session was active. We cannot call notify() here
        // (it would create a circular dependency through the store), so we dispatch
        // a custom DOM event. App.tsx listens for it and shows a toast.
        if (error.response?.status === 403) {
          const url: string = error.config?.url ?? "";
          if (url.includes("/projects/") || url.includes("/services/")) {
            window.dispatchEvent(
              new CustomEvent("avahana:access-changed", { detail: { url } })
            );
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic API methods
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error: any) {
      // Translate axios failures into our typed errors so callers keep the HTTP
      // status / backend code / details instead of a bare Error string.
      // See docs/error-handling.md.
      if (error.response) {
        // Server responded with a non-2xx status.
        const data = error.response.data;
        const message = data?.error || data?.message || "Server error";
        const code = data?.code || `HTTP_${error.response.status}`;
        throw new ApiError(message, error.response.status, code, data, error);
      } else if (error.request) {
        // Request was sent but no response came back (offline, timeout, CORS…).
        throw new NetworkError(undefined, error);
      } else {
        // Failure before the request was even made (config/programming error).
        throw new ApiError(
          error.message || "An unexpected error occurred",
          0,
          "REQUEST_SETUP",
          undefined,
          error
        );
      }
    }
  }

  private get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "GET", url });
  }

  private post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.request({ ...config, method: "POST", url, data });
  }

  private put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.request({ ...config, method: "PUT", url, data });
  }

  private patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.request({ ...config, method: "PATCH", url, data });
  }

  private delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "DELETE", url });
  }

  // Authentication APIs
  auth = {
    login: async (
      email: string,
      password: string
    ): Promise<{ user: any; token: string }> => {
      return this.post("/auth/login", { email, password });
    },

    register: async (
      email: string,
      password: string,
      name?: string
    ): Promise<{ user: any; token: string }> => {
      return this.post("/auth/register", { email, password, name });
    },

    profile: async (): Promise<{ user: any }> => {
      return this.get("/auth/me");
    },

    updateProfile: async (data: any): Promise<{ user: any }> => {
      return this.put("/auth/me", data);
    },

    logout: async (): Promise<void> => {
      localStorage.removeItem("token");
      localStorage.removeItem("avahana-token");
      localStorage.removeItem("user");
    },
  };

  // Project Management APIs
  projects = {
    // Get all projects for the authenticated user
    list: async (): Promise<ProjectsResponse> => {
      return this.get("/projects");
    },

    // Create a new project
    create: async (
      data: CreateProjectRequest
    ): Promise<{ message: string; project: Project }> => {
      return this.post("/projects", data);
    },

    // Get a specific project by ID
    get: async (
      id: string
    ): Promise<{ project: Project; deployments: any[] }> => {
      return this.get(`/projects/${id}`);
    },

    // Update a project
    update: async (
      id: string,
      data: UpdateProjectRequest
    ): Promise<{ message: string; project: Project }> => {
      return this.put(`/projects/${id}`, data);
    },

    // Partially update a project
    patch: async (
      id: string,
      data: Partial<Project>
    ): Promise<{ message: string; project: Project }> => {
      return this.patch(`/projects/${id}`, data);
    },

    // Delete a project
    delete: async (id: string): Promise<{ message: string }> => {
      return this.delete(`/projects/${id}`);
    },
  };

  // Environment Management APIs
  environments = {
    // Create a new environment
    create: async (
      data: CreateEnvironmentRequest
    ): Promise<{ success: boolean; data: Environment }> => {
      return this.post("/environments", data);
    },

    // Get a specific environment by ID
    get: async (
      id: string
    ): Promise<{ success: boolean; data: Environment }> => {
      return this.get(`/environments/${id}`);
    },

    // Update an environment
    update: async (
      id: string,
      data: UpdateEnvironmentRequest
    ): Promise<{ success: boolean; data: Environment }> => {
      return this.put(`/environments/${id}`, data);
    },

    // Delete an environment
    delete: async (
      id: string
    ): Promise<{ success: boolean; message: string }> => {
      return this.delete(`/environments/${id}`);
    },

    // List environments for a project
    listByProject: async (projectId: string): Promise<EnvironmentsResponse> => {
      return this.get(`/projects/${projectId}/environments`);
    },

    // Get default environment for a project
    getDefault: async (
      projectId: string
    ): Promise<{ success: boolean; data: Environment }> => {
      return this.get(`/projects/${projectId}/environments/default`);
    },

    // Set environment variable
    setVariable: async (
      id: string,
      key: string,
      value: string
    ): Promise<{ success: boolean; data: Environment }> => {
      return this.post(`/environments/${id}/variables`, { key, value });
    },

    // Delete environment variable
    deleteVariable: async (
      id: string,
      key: string
    ): Promise<{ success: boolean; data: Environment }> => {
      return this.delete(`/environments/${id}/variables/${key}`);
    },
  };

  // Analytics APIs
  analytics = {
    // Get project analytics for all environments
    getProjectAnalytics: async (
      projectId: string
    ): Promise<{ analytics: EnvironmentAnalytics[] }> => {
      return this.get(`/projects/${projectId}/analytics`);
    },

    // Get environment-specific analytics
    getEnvironmentAnalytics: async (
      projectId: string,
      environmentId: string
    ): Promise<{ analytics: EnvironmentAnalytics }> => {
      return this.get(
        `/projects/${projectId}/environments/${environmentId}/analytics`
      );
    },
  };

  // Git Integration APIs
  git = {
    // Connect flow. For GitHub, `method` selects "app" (GitHub App install,
    // per-repo grant — default) or "oauth" (full access, incl. shared repos;
    // only offered when enabled in platform settings).
    connect: async (
      provider: string,
      data: { redirect_uri: string; scopes?: string[]; method?: "app" | "oauth" }
    ) => {
      return this.post(`/git/connect/${provider}`, data);
    },

    // Git integration config (e.g. whether the OAuth option is offered)
    getConfig: async () => {
      return this.get("/git/config");
    },

    callback: async (provider: string, code: string, state: string) => {
      // Properly encode the query parameters to handle special characters in state
      const params = new URLSearchParams();
      params.append("code", code);
      params.append("state", state);
      return this.get(`/git/callback/${provider}?${params.toString()}`);
    },

    // Account management
    accounts: {
      list: async () => {
        return this.get("/git/accounts");
      },

      get: async (id: string) => {
        return this.get(`/git/accounts/${id}`);
      },

      disconnect: async (
        id: string,
        data: { revoke_token?: boolean; confirm: boolean }
      ) => {
        return this.delete(`/git/accounts/${id}`, { data });
      },

      sync: async (id: string) => {
        return this.put(`/git/accounts/${id}/sync`);
      },
    },

    // Repository management
    repositories: {
      list: async (filters?: {
        provider?: string;
        language?: string;
        search?: string;
        private?: boolean;
        limit?: number;
        skip?: number;
      }) => {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
              params.append(key, value.toString());
            }
          });
        }
        const queryString = params.toString();
        return this.get(
          `/git/repositories${queryString ? `?${queryString}` : ""}`
        );
      },

      getByAccount: async (accountId: string) => {
        return this.get(`/git/repositories/account/${accountId}`);
      },

      getBranches: async (id: string) => {
        return this.get(`/git/repositories/${id}/branches`);
      },
    },

    // Integration management
    integrations: {
      list: async () => {
        return this.get("/git/integrations");
      },

      create: async (data: {
        account_id: string;
        repository_id?: string;
        access_type: string;
        auto_deploy?: boolean;
        deploy_branch?: string;
        webhook_enabled?: boolean;
        repositories?: string[];
      }) => {
        return this.post("/git/integrations", data);
      },

      update: async (
        id: string,
        data: {
          auto_deploy?: boolean;
          deploy_branch?: string;
          repositories?: string[];
        }
      ) => {
        return this.put(`/git/integrations/${id}`, data);
      },

      delete: async (id: string) => {
        return this.delete(`/git/integrations/${id}`);
      },
    },

    // Webhooks
    webhooks: {
      create: async (
        repoId: string,
        data: { events: string[]; active?: boolean }
      ) => {
        return this.post(`/git/webhooks/${repoId}`, data);
      },

      delete: async (repoId: string) => {
        return this.delete(`/git/webhooks/${repoId}`);
      },
    },
  };

  // Services APIs
  services = {
    // Get services for a project, optionally filtered by environment
    list: async (
      projectId: string,
      environmentId?: string
    ): Promise<{ services: any[] }> => {
      const params = environmentId ? { environmentId } : {};
      return this.get(`/projects/${projectId}/services`, { params });
    },

    // Get a specific service by ID
    get: async (serviceId: string): Promise<any> => {
      // Backend returns service directly, not wrapped
      return this.get(`/services/${serviceId}`);
    },

    // Create a new service
    create: async (projectId: string, data: any): Promise<{ service: any }> => {
      return this.post(`/projects/${projectId}/services`, data);
    },

    // Update a service
    update: async (serviceId: string, data: any): Promise<{ service: any }> => {
      return this.put(`/services/${serviceId}`, data);
    },

    // Delete a service
    delete: async (serviceId: string): Promise<{ services: any[] }> => {
      return this.delete(`/services/${serviceId}`);
    },

    // Service management
    start: async (
      serviceId: string
    ): Promise<{ message: string; serviceId: string; status: string }> => {
      return this.post(`/services/${serviceId}/start`);
    },

    stop: async (
      serviceId: string
    ): Promise<{ message: string; serviceId: string; status: string }> => {
      return this.post(`/services/${serviceId}/stop`);
    },

    restart: async (
      serviceId: string
    ): Promise<{ message: string; serviceId: string; status: string }> => {
      return this.post(`/services/${serviceId}/restart`);
    },

    redeploy: async (
      serviceId: string,
      reason?: string
    ): Promise<{ message: string; deploymentId: string }> => {
      return this.post(`/services/${serviceId}/redeploy`, { reason: reason ?? "manual" });
    },


    status: async (
      serviceId: string
    ): Promise<{
      serviceId: string;
      status: string;
      health: string;
      uptime: string;
      lastChecked: string;
      deployments: any[];
    }> => {
      return this.get(`/services/${serviceId}/status`);
    },

    logs: async (
      serviceId: string,
      lines?: number
    ): Promise<{
      serviceId: string;
      lines: number;
      logs: string[];
      lastUpdated: string;
    }> => {
      const params = lines ? { lines } : {};
      return this.get(`/services/${serviceId}/logs`, { params });
    },

    // Opens a live pod log stream via Server-Sent Events.
    // WHY EventSource factory: same reason as deployments.streamLogs — the browser
    // EventSource API is a persistent connection, not a request/response. Caller
    // owns the lifecycle (close on unmount or tab switch).
    streamPodLogs: (serviceId: string, tail = 100): EventSource => {
      const token =
        localStorage.getItem("avahana-token") ||
        localStorage.getItem("token") ||
        "";
      return new EventSource(
        `${API_BASE_URL}/services/${encodeURIComponent(serviceId)}/logs/stream?token=${encodeURIComponent(token)}&tail=${tail}`
      );
    },

    // Get service credentials from MongoDB (TEMPORARY: for testing)
    // TODO: Will fetch from Kubernetes secrets in production
    credentials: async (serviceId: string): Promise<any> => {
      // Fetch credentials directly using the service ID
      return this.get(`/services/${serviceId}/credentials`);
    },

    // Enable or disable automatic deployment on git push.
    // When enabled, the backend registers a GitHub webhook and sets autoDeploy.branch.
    updateAutoDeploy: async (
      serviceId: string,
      enabled: boolean,
      branch?: string
    ): Promise<{ enabled: boolean; branch?: string; repo?: string }> => {
      return this.patch(`/services/${serviceId}/auto-deploy`, { enabled, branch });
    },

    // Get deployments for a specific service
    deployments: async (serviceId: string): Promise<any[]> => {
      try {
        // Use the dedicated endpoint for service deployments
        const response: any = await this.get(
          `/services/${serviceId}/deployments`
        );
        return response.deployments || [];
      } catch (error) {
        log.error("Failed to fetch service deployments", error);
        return [];
      }
    },
  };

  // Deployment APIs (placeholder for future implementation)
  deployments = {
    list: async () => {
      return this.get("/deployments");
    },

    create: async (data: any) => {
      return this.post("/deployments", data);
    },

    get: async (id: string) => {
      return this.get(`/deployments/${id}`);
    },

    getById: async (id: string): Promise<{ deployment: any }> => {
      return this.get(`/deployments/${id}`);
    },

    getLogs: async (id: string) => {
      return this.get(`/deployments/${id}/pipeline/logs`);
    },

    delete: async (id: string) => {
      return this.delete(`/deployments/${id}`);
    },

    // Opens an EventSource for the deployment log stream.
    // WHY a factory instead of wrapping in the axios client: EventSource is a browser
    // API that maintains a persistent connection — it doesn't fit the request/response
    // model of axios. The caller owns the EventSource lifecycle (close on unmount).
    streamLogs: (serviceId: string, deploymentId: string): EventSource => {
      const token =
        localStorage.getItem("avahana-token") ||
        localStorage.getItem("token") ||
        "";
      return new EventSource(
        `${API_BASE_URL}/services/${encodeURIComponent(serviceId)}/deployments/${encodeURIComponent(deploymentId)}/stream?token=${encodeURIComponent(token)}`
      );
    },
  };

  // Environment variables — per-service, per-environment
  env = {
    get: async (
      serviceId: string,
      environment = "development"
    ): Promise<{ envVars: Record<string, string>; environment: string; count: number; envVarsPendingDeploy: boolean }> => {
      return this.get(`/services/${serviceId}/env`, { params: { environment } });
    },

    // Merge update: keys with non-empty values are set, keys with "" are deleted.
    update: async (
      serviceId: string,
      envVars: Record<string, string>,
      environment = "development"
    ): Promise<{ envVars: Record<string, string>; environment: string; envVarsPendingDeploy: boolean }> => {
      return this.put(`/services/${serviceId}/env`, { envVars }, { params: { environment } });
    },

    delete: async (
      serviceId: string,
      key: string,
      environment = "development"
    ): Promise<{ envVars: Record<string, string>; environment: string; envVarsPendingDeploy: boolean }> => {
      return this.delete(`/services/${serviceId}/env/${encodeURIComponent(key)}`, { params: { environment } });
    },
  };

  // Custom domains
  domains = {
    list: async (serviceId: string): Promise<{ domains: any[] }> => {
      return this.get(`/services/${serviceId}/domains`);
    },

    add: async (serviceId: string, domain: string): Promise<{ domain: any }> => {
      return this.post(`/services/${serviceId}/domains`, { domain });
    },

    remove: async (serviceId: string, domain: string): Promise<void> => {
      return this.delete(`/services/${serviceId}/domains/${encodeURIComponent(domain)}`);
    },
  };

  // Health check
  health = {
    check: async (): Promise<{ status: string; service: string }> => {
      return this.get("/health");
    },
  };

  // Backups — Phase 1 (config). Database/volume return 422 until Phase 2/3.
  backups = {
    trigger: async (
      serviceId: string,
      type = "config"
    ): Promise<{ backup: import("../types").Backup }> => {
      return this.post(`/services/${serviceId}/backups`, { type });
    },

    list: async (
      serviceId: string,
      page = 1,
      limit = 20
    ): Promise<{
      backups: import("../types").Backup[];
      pagination: import("../types").BackupPagination;
    }> => {
      return this.get(`/services/${serviceId}/backups`, { params: { page, limit } });
    },

    get: async (
      serviceId: string,
      backupId: string
    ): Promise<{ backup: import("../types").Backup }> => {
      return this.get(`/services/${serviceId}/backups/${backupId}`);
    },

    delete: async (serviceId: string, backupId: string): Promise<{ message: string }> => {
      return this.delete(`/services/${serviceId}/backups/${backupId}`);
    },

    downloadUrl: (serviceId: string, backupId: string): string => {
      const token =
        localStorage.getItem("avahana-token") ||
        localStorage.getItem("token") ||
        "";
      return `${API_BASE_URL}/services/${encodeURIComponent(serviceId)}/backups/${encodeURIComponent(backupId)}/download?token=${encodeURIComponent(token)}`;
    },

    schedules: {
      create: async (
        serviceId: string,
        data: {
          type?: string;
          cronExpression: string;
          enabled: boolean;
          retentionDays: number;
        }
      ): Promise<{ schedule: import("../types").BackupSchedule }> => {
        return this.post(`/services/${serviceId}/backup-schedules`, data);
      },

      list: async (
        serviceId: string
      ): Promise<{ schedules: import("../types").BackupSchedule[] }> => {
        return this.get(`/services/${serviceId}/backup-schedules`);
      },

      update: async (
        serviceId: string,
        scheduleId: string,
        data: Partial<import("../types").BackupSchedule>
      ): Promise<{ schedule: import("../types").BackupSchedule }> => {
        return this.put(`/services/${serviceId}/backup-schedules/${scheduleId}`, data);
      },

      delete: async (serviceId: string, scheduleId: string): Promise<{ message: string }> => {
        return this.delete(`/services/${serviceId}/backup-schedules/${scheduleId}`);
      },
    },
  };
}

// Export singleton instance
export const api = new ApiClient();

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem("token");
  return !!token;
};

// Helper function to get current user
export const getCurrentUser = (): any | null => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

// Helper function to set auth data
export const setAuthData = (token: string, user: any): void => {
  localStorage.setItem("token", token);
  localStorage.setItem("user", JSON.stringify(user));
};

// Helper function to clear auth data
export const clearAuthData = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};
